import { useState, useMemo, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Bell, CheckCheck, Megaphone, Settings } from 'lucide-react';
import clsx from 'clsx';

import { PageContainer } from '@/shared/components/layout/PageContainer';
import { AppButton } from '@/shared/components/ui/AppButton';
import {
  NotificationList,
  getNotificationCategory,
} from '@/shared/components/promote/NotificationList';
import type { Notification } from '@/shared/types/entities';
import { useAuthStore } from '@/shared/stores/auth-store';
import { useNotificationsStore } from '@/shared/stores/notifications-store';

import { useNotifications } from '../../api/notifications-queries';
import {
  useMarkNotificationRead,
  useMarkAllNotificationsRead,
} from '../../api/notifications-mutations';
import { NotificationPreferencesDialog } from '../../components/NotificationPreferencesDialog';
import type { NotificationFilterType } from '../../types/notifications.types';
import styles from './NotificationsPage.module.scss';

const FILTER_TABS: { value: NotificationFilterType; label: string; icon: React.ReactNode }[] = [
  { value: 'all', label: 'All', icon: <Bell size={14} /> },
  { value: 'campaign', label: 'Campaign', icon: <Megaphone size={14} /> },
  { value: 'system', label: 'System', icon: <Settings size={14} /> },
];

export function NotificationsPage() {
  const [filter, setFilter] = useState<NotificationFilterType>('all');
  const [preferencesOpen, setPreferencesOpen] = useState(false);
  const orgId = useAuthStore((s) => s.orgId);

  const {
    data: notificationsData,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useNotifications();
  const markReadMutation = useMarkNotificationRead();
  const markAllReadMutation = useMarkAllNotificationsRead();

  const allNotifications = useMemo(
    () => notificationsData?.pages.flatMap((page) => page.data) ?? [],
    [notificationsData],
  );

  const filteredNotifications = useMemo(() => {
    if (filter === 'all') return allNotifications;
    return allNotifications.filter((n) => getNotificationCategory(n.type) === filter);
  }, [allNotifications, filter]);

  const unreadCount = useNotificationsStore((s) => s.unreadCount);

  const handleMarkRead = useCallback(
    (notification: Notification) => {
      if (!notification.isRead) {
        markReadMutation.mutate(notification.id);
      }
    },
    [markReadMutation],
  );

  const handleMarkAllRead = useCallback(() => {
    markAllReadMutation.mutate();
  }, [markAllReadMutation]);

  return (
    <PageContainer
      title="Notifications"
      subtitle={unreadCount > 0 ? `${unreadCount} unread` : 'All caught up'}
      actions={
        <div className={styles.headerActions}>
          {unreadCount > 0 && (
            <AppButton
              variant="secondary"
              size="sm"
              onClick={handleMarkAllRead}
              loading={markAllReadMutation.isPending}
              disabled={!orgId}
            >
              <CheckCheck size={14} />
              Mark All Read
            </AppButton>
          )}
          <AppButton
            variant="secondary"
            size="sm"
            onClick={() => setPreferencesOpen(true)}
            disabled={!orgId}
          >
            <Settings size={14} />
            Settings
          </AppButton>
        </div>
      }
    >
      <div className={styles.container}>
        <div className={styles.filterBar}>
          {FILTER_TABS.map((tab) => (
            <button
              key={tab.value}
              type="button"
              className={clsx(styles.filterTab, filter === tab.value && styles.filterTabActive)}
              onClick={() => setFilter(tab.value)}
            >
              {tab.icon}
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        {isLoading && (
          <div className={styles.loadingState}>
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className={styles.skeleton} style={{ height: 72 }} />
            ))}
          </div>
        )}

        {isError && (
          <div className={styles.errorState}>
            <p>Failed to load notifications.</p>
          </div>
        )}

        {!isLoading && !isError && filteredNotifications.length === 0 && (
          <motion.div
            className={styles.emptyState}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            <Bell size={48} />
            <h3 className={styles.emptyTitle}>
              {filter === 'all' ? 'No notifications' : `No ${filter} notifications`}
            </h3>
            <p className={styles.emptyDescription}>
              {filter === 'all'
                ? "You're all caught up! Notifications will appear here when there are updates."
                : `No ${filter} notifications to show right now.`}
            </p>
          </motion.div>
        )}

        {!isLoading && !isError && filteredNotifications.length > 0 && (
          <NotificationList notifications={filteredNotifications} onMarkRead={handleMarkRead} />
        )}

        {!isLoading && !isError && hasNextPage && (
          <div className={styles.loadMore}>
            <AppButton
              variant="secondary"
              size="sm"
              onClick={() => fetchNextPage()}
              loading={isFetchingNextPage}
            >
              Load more
            </AppButton>
          </div>
        )}
      </div>

      <NotificationPreferencesDialog open={preferencesOpen} onOpenChange={setPreferencesOpen} />
    </PageContainer>
  );
}
